import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle, Activity, Heart, BookOpen, Users, Ear } from 'lucide-react';

const WhoItsForPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-white pt-24 pb-16">
            
            {/* Hero Block */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
                <h1 className="text-4xl md:text-6xl font-bold text-sage-900 mb-6 font-serif">
                    Who NatureNani Is For
                </h1>
                <p className="text-xl md:text-2xl font-medium text-sage-800 max-w-2xl mx-auto">
                    NatureNani was built for people who want to understand, not just manage.
                </p>
            </div>

            {/* Intro Paragraph */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
                <p className="text-lg text-sage-700 leading-relaxed text-center sm:text-left">
                    Most wellness tools are built around symptoms. NatureNani is built around you — your constitution, your patterns, your questions. If you've ever felt that your body is trying to tell you something and you didn't have the framework to understand it, you're exactly who we built this for.
                </p>
            </div>

            {/* Audience Cards Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
                <div className="mb-12 text-center sm:text-left">
                    <span className="text-sm font-bold tracking-widest text-sage-500 uppercase">This is for you if...</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {[
                        {
                            icon: <HelpCircle size={24} className="text-[#B7791F]" />,
                            lead: 'You keep asking "why"',
                            support: "Why am I always tired? Why does stress hit me harder than it used to? Why do I keep getting the same imbalances? If conventional settings haven't given you satisfying answers, Ayurveda offers a different lens entirely."
                        },
                        {
                            icon: <Activity size={24} className="text-[#B7791F]" />,
                            lead: "You're managing a chronic condition",
                            support: "You want natural, evidence-aligned support that works alongside your medical care — not against it. NatureNani gives you the ancient framework your doctor's appointment doesn't have time for."
                        },
                        {
                            icon: <Heart size={24} className="text-[#B7791F]" />,
                            lead: "You're curious about your Dosha",
                            support: "You've heard about Vata, Pitta, and Kapha — and you want to know what your specific constitution means for your diet, your energy, your tendencies, and your path to balance."
                        },
                        {
                            icon: <BookOpen size={24} className="text-[#B7791F]" />,
                            lead: "You want guidance rooted in tradition",
                            support: "Not trends. Not influencer advice. Not generic supplement recommendations. You want wellness guidance that traces back to primary sources — the Charaka Samhita, the Ashtanga Hridayam, five thousand years of documented clinical wisdom."
                        },
                        {
                            icon: <Users size={24} className="text-[#B7791F]" />,
                            lead: "You're thinking about your family",
                            support: "You want natural, thoughtful support for your family's everyday health — grounded in tradition, not guesswork."
                        },
                        {
                            icon: <Ear size={24} className="text-[#B7791F]" />,
                            lead: "You sense your body is communicating",
                            support: "You've always felt that your symptoms follow patterns — and you want the framework to finally understand what they mean."
                        }
                    ].map((item, idx) => (
                        <div key={idx} className="flex gap-4 p-6 rounded-xl border border-sage-100 bg-sage-50/50 hover:shadow-md transition-shadow">
                            <div className="flex-shrink-0 mt-1">
                                {item.icon}
                            </div>
                            <div>
                                <h3 className="font-bold text-sage-900 text-lg mb-2">{item.lead}</h3>
                                <p className="text-sage-600 leading-relaxed">{item.support}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Divider Block */}
            <section className="w-full bg-sage-50 py-20 px-4 sm:px-6 lg:px-8 mb-24 border-y border-sage-100">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-sage-900 mb-6">
                        Not a replacement. A deeper understanding.
                    </h2>
                    <div className="text-lg text-sage-700 leading-relaxed space-y-4">
                        <p>
                            NatureNani is designed to live alongside your regular care. Whether you're addressing common imbalances, supporting recovery, managing a chronic condition holistically, or simply deepening your self-knowledge — NatureNani gives you the ancient framework that modern healthcare was never built to provide.
                        </p>
                        <p className="font-medium text-sage-900">
                            We are not here to replace your doctor. We are here to help you understand your body deeply enough to have better conversations with one.
                        </p>
                    </div>
                </div>
            </section>

            {/* Know Your Nature Section (Moved from About Page) */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
                <div className="bg-sage-50 p-8 md:p-12 rounded-2xl border border-sage-100 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-2 h-full bg-[#2D6A4F]"></div>
                    <h2 className="text-3xl font-bold text-sage-900 mb-6 flex items-center gap-3">
                        <span className="text-3xl">🌿</span> Know Your Nature
                    </h2>
                    <div className="text-lg text-sage-700 leading-relaxed space-y-5">
                        <p>
                            The Sanskrit word for your unique mind-body constitution is <span className="font-semibold text-sage-900">Prakriti</span> — your nature, set at birth, as individual as your fingerprint.
                        </p>
                        <p>
                            Ayurveda teaches that most imbalance — fatigue, digestive issues, skin flare-ups, anxiety, poor sleep — is not random. It follows patterns that are specific to your constitution. A Vata person and a Kapha person experiencing the same symptom may need completely different support. Understanding which you are changes everything.
                        </p>
                        <p>
                            NatureNani's Wellness Journey walks you through an interactive body-mapping intake that identifies your Dosha and builds your personal Wellness Passport — a persistent health profile that shapes every consultation, herbal protocol, meal plan, and yoga sequence Nani creates for you.
                        </p>
                        <p className="italic font-bold text-[#B7791F] text-xl border-l-4 border-sage-300 pl-4 mt-8 pt-2">
                            No two Wellness Profiles are the same. Because no two bodies are.
                        </p>
                    </div>
                </div>
            </section>

            {/* Bottom CTA Block */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center pb-12">
                <h2 className="text-3xl font-bold text-sage-900 mb-4">
                    Ready to know your nature?
                </h2>
                <p className="text-sage-600 mb-10 leading-relaxed max-w-2xl mx-auto">
                    Your Wellness Journey takes about 5 minutes. It identifies your Dosha, builds your personal Wellness Passport, and unlocks personalized guidance across herbs, movement, and nutrition.
                </p>
                <div className="flex flex-col items-center gap-6">
                    <Link
                        to="/onboarding"
                        className="inline-flex items-center justify-center px-8 py-4 bg-[#2D6A4F] text-white rounded-full font-bold text-lg hover:bg-[#1B4332] transition-colors shadow-lg transform hover:-translate-y-1"
                    >
                        Take the Wellness Assessment <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                    <a
                        href="/#how-it-works"
                        className="text-[#B7791F] font-bold hover:underline transition-all"
                    >
                        Learn how NatureNani works →
                    </a>
                </div>
            </section>

        </div>
    );
};

export default WhoItsForPage;
